import { useState } from "react";
import type { FilterOptions } from "@/utils/media";

interface FilterBarProps {
  genres: string[];
  years: number[];
  regions: string[];
  value: FilterOptions;
  onChange: (next: FilterOptions) => void;
  onReset: () => void;
}

const SORTS: { key: FilterOptions["sort"]; label: string }[] = [
  { key: "hot", label: "按热度" },
  { key: "new", label: "按更新时间" },
  { key: "rating", label: "按评分" },
];

export default function FilterBar({ genres, years, regions, value, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const toggle = <K extends "genres" | "years" | "regions">(key: K, item: FilterOptions[K][number]) => {
    const current = value[key] as FilterOptions[K][number][];
    const next = current.includes(item)
      ? current.filter((entry) => entry !== item)
      : [...current, item];
    onChange({ ...value, [key]: next } as FilterOptions);
  };

  const activeCount = value.genres.length + value.years.length + value.regions.length;

  const chipClass = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer border ${
      active
        ? "bg-primary-500 border-primary-500 text-background-50 font-medium"
        : "bg-background-200/60 border-background-300 text-foreground-700 hover:border-primary-400 hover:text-foreground-950"
    }`;

  const body = (
    <div className="space-y-5">
      <div>
        <p className="text-xs text-foreground-600 mb-2.5 font-label">类型（可多选）</p>
        <div className="flex flex-wrap gap-2">
          {genres.map((genre) => (
            <button key={genre} type="button" onClick={() => toggle("genres", genre)} className={chipClass(value.genres.includes(genre))}>
              {genre}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs text-foreground-600 mb-2.5 font-label">年份</p>
        <div className="flex flex-wrap gap-2">
          {years.map((year) => (
            <button key={year} type="button" onClick={() => toggle("years", year)} className={chipClass(value.years.includes(year))}>
              {year}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs text-foreground-600 mb-2.5 font-label">地区</p>
        <div className="flex flex-wrap gap-2">
          {regions.map((region) => (
            <button key={region} type="button" onClick={() => toggle("regions", region)} className={chipClass(value.regions.includes(region))}>
              {region}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-background-200">
        <span className="text-xs text-foreground-600 mr-1 font-label">排序</span>
        {SORTS.map((sort) => (
          <button
            key={sort.key}
            type="button"
            onClick={() => onChange({ ...value, sort: sort.key })}
            className={`px-3 py-1.5 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
              value.sort === sort.key ? "bg-secondary-500/20 text-secondary-300 font-medium" : "text-foreground-700 hover:bg-background-200/60"
            }`}
          >
            {sort.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onReset}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs md:text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line" />
          重置筛选
        </button>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lg:hidden w-full flex items-center justify-between text-sm font-medium text-foreground-950 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-400" />
          筛选与排序
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-label">{activeCount}</span>
          )}
        </span>
        <i className={open ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"} />
      </button>
      <div className={`${open ? "block mt-4" : "hidden"} lg:block lg:mt-0`}>{body}</div>
    </div>
  );
}